/**
 * Query client configuration.
 *
 * The craft bar for this product is a web app that feels faster than a native
 * one, and almost all of that feeling is decided here rather than in any
 * component. Three choices do the work:
 *
 * 1. **Data is served from cache instantly and revalidated behind the scenes.**
 *    A parent opening the schedule for the third time today should see it in
 *    the same frame, not after a spinner.
 *
 * 2. **A 401 never retries.** Retrying an expired session three times with
 *    backoff turns "please sign in" into four seconds of nothing.
 *
 * 3. **Mutations are optimistic by default at the call site.** The RSVP control
 *    is the most-tapped thing in the product and it must commit visually on
 *    press. `onMutate` snapshots, `onError` rolls back, `onSettled`
 *    revalidates — the pattern is in `useOptimistic*` hooks per feature.
 */
import { QueryClient } from "@tanstack/react-query";
import { ApiError } from "./api";

export const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      // Fresh for half a minute: long enough that tabbing between roster and
      // schedule is instant, short enough that a coach's edit shows up.
      staleTime: 30_000,
      gcTime: 5 * 60_000,
      retry: (failureCount, error) => {
        // An auth failure or a client mistake will not fix itself.
        if (error instanceof ApiError && (error.isAuth || error.status < 500)) return false;
        return failureCount < 2;
      },
      refetchOnWindowFocus: true,
      // The schedule changes while a parent is standing on a touchline with a
      // locked phone. Refetching on reconnect is what makes the app correct
      // after a tunnel or a dead patch of signal.
      refetchOnReconnect: true,
    },
    mutations: {
      retry: false,
    },
  },
});

/**
 * Query keys in one place. Scattered key strings are how cache invalidation
 * quietly stops working: one caller writes `["events", teamId]` and another
 * `["team", teamId, "events"]`, and neither ever invalidates the other.
 */
export const qk = {
  me: () => ["me"] as const,
  teams: () => ["teams"] as const,
  team: (id: string) => ["team", id] as const,
  roster: (teamId: string) => ["team", teamId, "roster"] as const,
  member: (teamId: string, memberId: string) => ["team", teamId, "member", memberId] as const,
  events: (teamId: string, range?: string) => ["team", teamId, "events", range ?? "all"] as const,
  event: (eventId: string) => ["event", eventId] as const,
  availability: (eventId: string) => ["event", eventId, "availability"] as const,
  /** The family's merged calendar across every team their children are on. */
  myCalendar: (range?: string) => ["my", "calendar", range ?? "upcoming"] as const,
  conversations: (scopeId: string) => ["conversations", scopeId] as const,
  messages: (conversationId: string) => ["conversation", conversationId, "messages"] as const,
  invoices: (householdId: string) => ["household", householdId, "invoices"] as const,
  org: (id: string) => ["org", id] as const,
  orgTeams: (id: string) => ["org", id, "teams"] as const,
} as const;
